import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, C, fmtChips } from "../../lib/api";
import { DEALERS } from "./Dealer";
import { Verifier } from "./Verifier";
import { voiceEnabled } from "../../hooks/useDealerLines";
import { AssetImage } from "../AssetImage";
import { FELT_IDS } from "../../assets/assetManifest";
import { useAuth } from "../../context/AuthContext";

export const SettingsPanel = ({ open, onClose, lastResult, prevResult, skin, setSkin, felt, setFelt, tableId, myUserId, openTab }) => {
  const [tab, setTab] = useState("verify");
  const guest = !!useAuth().user?.guest;
  useEffect(() => { if (openTab) { setTab(openTab.tab); setSelected(null); } }, [openTab]);
  const [hands, setHands] = useState([]);
  const [selected, setSelected] = useState(null);
  const [voice, setVoice] = useState(voiceEnabled());
  useEffect(() => {
    if (!open || tab !== "history") return;
    api.get(C.ApiRoutes.HANDS, { params: { table_id: tableId, limit: 20 } }).then((r) => setHands(r.data)).catch(() => {});
  }, [open, tab, tableId, lastResult?.hand_id]);
  // verify the newest hand whose seed is public; a hand stays sealed until the next one has started
  const pick = selected || prevResult;
  const current = pick && !pick.sealed ? pick : null;
  const sealed = (selected?.sealed ? selected : null) || (lastResult?.sealed ? lastResult : null);
  return (
    <aside data-testid="settings-panel" aria-hidden={!open} className={`fixed top-0 right-0 h-full w-96 z-50 glass flex flex-col transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)] ${open ? "translate-x-0" : "translate-x-full pointer-events-none"}`}>
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <h2 className="text-base">Table settings</h2>
        <button data-testid="settings-close" onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-1 p-3">
        {[["verify", "Provably fair"], ["dealer", "Dealer"], ["history", "Hands"]].map(([k, l]) => (
          <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 text-xs py-2 rounded-full ${tab === k ? "bg-amber-500 text-slate-900" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{l}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto pane-scroll p-5">
        {tab === "verify" && <Verifier result={current} sealed={sealed} myUserId={myUserId} />}
        {tab === "dealer" && (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-slate-400">Cosmetic only — only you see your dealer.</p>
            {Object.entries(DEALERS).map(([k, d]) => (
              <button key={k} data-testid={`dealer-skin-${k}`} onClick={() => { localStorage.setItem("pfp_dealer", k); setSkin(k); }}
                className={`flex items-center gap-3 p-2 rounded-xl border ${skin === k ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
                <AssetImage id={d.asset} width={48} className="rounded-full" />
                <div className="text-left"><div className="text-sm">{d.name}</div><div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div></div>
              </button>
            ))}
            <p className="text-xs text-slate-400 mt-3">Table felt{guest ? " · members only" : ""}</p>
            <div className={`flex gap-2 ${guest ? "opacity-40 pointer-events-none" : ""}`} data-testid="settings-felt-picker">
              {FELT_IDS.map((id) => (
                <button key={id} data-testid={`felt-${id.split("_")[2]}`} onClick={() => { localStorage.setItem("pfp_felt", id); setFelt(id); }}
                  className={`flex-1 rounded-xl border overflow-hidden ${felt === id ? "border-amber-400" : "border-white/10 hover:border-white/30"}`}>
                  <AssetImage id={id} width={110} className="w-full h-12 object-cover" />
                </button>
              ))}
            </div>
            <label className="flex items-center justify-between glass rounded-xl p-3 mt-2 text-sm cursor-pointer">
              <span>Dealer voice announcements</span>
              <input data-testid="dealer-voice-toggle" type="checkbox" checked={voice} onChange={(e) => { localStorage.setItem("pfp_dealer_voice", e.target.checked ? "on" : "off"); setVoice(e.target.checked); }} className="accent-amber-500 w-4 h-4" />
            </label>
          </div>
        )}
        {tab === "history" && (
          <div className="flex flex-col gap-2">
            {hands.length === 0 && <p className="text-xs text-slate-400">No hands recorded yet.</p>}
            {hands.map((h) => {
              const me = h.players.find((p) => p.user_id === myUserId);
              return (
                <button key={h.hand_id} data-testid={`hand-row-${h.hand_no}`} onClick={() => { setSelected(h); setTab("verify"); }}
                  className="text-left glass rounded-lg p-3 hover:border-amber-400/50">
                  <div className="flex justify-between text-xs"><span>#{h.hand_no} · {h.reason === "SHOWDOWN" ? "Showdown" : "Uncontested"}</span>
                    {me && <span className={`font-mono ${me.net >= 0 ? "text-emerald-300" : "text-red-300"}`}>{me.net >= 0 ? "+" : ""}{fmtChips(me.net)}</span>}</div>
                  <div className="font-mono text-[10px] text-slate-400 mt-1">{h.board.join(" ") || "no flop"}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};
